/**
 * PanelShell — the frame shared by admin.mypcb.com, risk.mypcb.com and sysops.mypcb.com
 * (Bill 2026-11-03, approved redesign).
 *
 * Desktop (900px and wider): a dark-blue sidebar (logo, app name and version; the platform
 * switcher when the app has platforms; page search with Ctrl K; Home; each section with its pages)
 * and a white top bar (breadcrumbs, API version, the app's extras such as the guided-tour button,
 * the "?" help for this page, and the account chip with About and Sign out).
 * Under 900px the sidebar becomes a drawer behind a 44px menu button (focus stays inside it,
 * Escape closes it, choosing a page closes it).
 *
 * The app decides what is in the menu; the shell only draws it. It is router-agnostic: the app
 * passes its Link component, navigate() and the current pathname.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { deriveBreadcrumbs, findPage, prettySegment } from './breadcrumbs';
import { useFocusTrap } from './focus';
import { ShellIcon } from './icons';
import { ModalShell } from './ModalShell';
import { SearchBox } from './SearchBox';
import type { Crumb, SearchItem, ShellLinkComponent, ShellNav, ShellPage, ShellPlatform, ShellSection } from './types';
import logoUrl from './assets/pcb-logo-white.svg';

export interface PanelShellUser {
  displayName: string;
  /** Second line in the account menu, e.g. the UPN or the role. */
  detail?: string;
}

export interface PanelShellProps {
  /** Short app name beside the logo: "Admin", "Risk Management", "SysOps". */
  appName: string;
  version: string;
  pathname: string;
  Link: ShellLinkComponent;
  navigate: (to: string) => void;
  /** Menu for the current platform (admin) or the app (risk, sysops). Null: no platform chosen yet. */
  nav: ShellNav | null;
  /** Platforms for the switcher. Leave out (risk, sysops) to hide the switcher. */
  platforms?: ReadonlyArray<ShellPlatform>;
  currentPlatformId?: string | null;
  /** Everything the search can find (pages and tasks, across platforms the person can use). */
  searchItems: ReadonlyArray<SearchItem>;
  /** e.g. "pcb-loans-api 0.195.0", shown in the top bar. */
  apiVersion?: string | null;
  user: PanelShellUser | null;
  onSignOut: () => void;
  aboutPath?: string;
  /** Extra top-bar controls (the guided-tour button). */
  topbarExtras?: ReactNode;
  /** Crumbs to use instead of the derived ones (pages outside every platform, e.g. About). */
  crumbs?: ReadonlyArray<Crumb>;
  /** True where every change asks for a reason (admin, risk): the help says so. */
  changesNeedReason?: boolean;
  /** data-tour targets kept from the old shells. */
  homeTour?: string;
  userTour?: string;
  children: ReactNode;
}

const PHONE_QUERY = '(max-width: 899.98px)';

function useIsPhone(): boolean {
  const get = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches;
  const [phone, setPhone] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(PHONE_QUERY);
    const on = (): void => setPhone(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

export function initialsOf(name: string): string {
  const parts = name
    .replace(/[(@].*$/, '')
    .split(/[\s.,_-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]!.charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : '';
  return (first + last).toUpperCase();
}

/** Sections with more pages than this collapse in the sidebar unless they hold the open page. */
const COLLAPSE_OVER = 8;

function SidebarSection({
  section,
  activePath,
  Link,
  onPick,
}: {
  section: ShellSection;
  activePath: string | null;
  Link: ShellLinkComponent;
  onPick: () => void;
}): JSX.Element | null {
  const pages = section.pages.filter((p) => !p.hidden);
  const holdsActive = pages.some((p) => p.path === activePath);
  const collapsible = pages.length > COLLAPSE_OVER;
  const [expanded, setExpanded] = useState(!collapsible || holdsActive);
  useEffect(() => {
    if (holdsActive) setExpanded(true);
  }, [holdsActive]);
  if (pages.length === 0) {
    // A section with nothing but its landing page (e.g. Hub actions not in the portal yet).
    return section.path ? (
      <div className="ps-nav__section">
        <Link to={section.path} className="ps-nav__heading ps-nav__heading--link" onClick={onPick}>
          {section.label}
        </Link>
      </div>
    ) : null;
  }

  const groups = new Map<string, ShellPage[]>();
  for (const p of pages) groups.set(p.group ?? '', [...(groups.get(p.group ?? '') ?? []), p]);
  const showGroups = collapsible && groups.size > 1;
  const listId = `ps-sec-${section.id}`;

  const link = (p: ShellPage): JSX.Element =>
    p.external ? (
      <a key={p.path} href={p.path} target="_blank" rel="noopener noreferrer" className="ps-nav__link" onClick={onPick}>
        {p.label}
        <ShellIcon name="external" size={12} />
        <span className="ps-visually-hidden"> (opens in a new tab)</span>
      </a>
    ) : (
      <Link
        key={p.path}
        to={p.path}
        className={`ps-nav__link${p.path === activePath ? ' ps-nav__link--active' : ''}`}
        aria-current={p.path === activePath ? 'page' : undefined}
        onClick={onPick}
      >
        {p.label}
      </Link>
    );

  return (
    <div className="ps-nav__section">
      {collapsible ? (
        <button
          type="button"
          className="ps-nav__heading ps-nav__heading--toggle"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((x) => !x)}
        >
          <span>{section.label}</span>
          <span className="ps-nav__count">{pages.length}</span>
          <ShellIcon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
        </button>
      ) : section.path ? (
        <Link to={section.path} className="ps-nav__heading ps-nav__heading--link" onClick={onPick}>
          {section.label}
        </Link>
      ) : (
        <span className="ps-nav__heading">{section.label}</span>
      )}
      <div id={listId} className="ps-nav__pages" hidden={!expanded}>
        {showGroups
          ? [...groups.entries()].map(([g, ps]) => (
              <div key={g} className="ps-nav__group">
                {g && <span className="ps-nav__group-label">{g}</span>}
                {ps.map(link)}
              </div>
            ))
          : pages.map(link)}
      </div>
    </div>
  );
}

export function PanelShell({
  appName,
  version,
  pathname,
  Link,
  navigate,
  nav,
  platforms,
  currentPlatformId,
  searchItems: items,
  apiVersion,
  user,
  onSignOut,
  aboutPath,
  topbarExtras,
  crumbs: crumbsOverride,
  changesNeedReason,
  homeTour,
  userTour,
  children,
}: PanelShellProps): JSX.Element {
  const phone = useIsPhone();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  const drawerInitial = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  useFocusTrap(sidebarRef, phone && drawerOpen, closeDrawer, drawerInitial);

  // Choosing a page (any navigation) closes the drawer and the account menu.
  useEffect(() => {
    setDrawerOpen(false);
    setMenuOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!phone) setDrawerOpen(false);
  }, [phone]);
  useEffect(() => {
    document.documentElement.classList.toggle('ps-drawer-open', phone && drawerOpen);
    return () => document.documentElement.classList.remove('ps-drawer-open');
  }, [phone, drawerOpen]);

  // Ctrl K / Cmd K: page search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (phone) {
          drawerInitial.current = searchRef.current;
          setDrawerOpen(true);
        }
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phone]);

  // The account menu closes on Escape or a click elsewhere.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const hit = useMemo(() => (nav ? findPage(pathname, nav) : null), [pathname, nav]);
  const activePath = hit?.page.path ?? null;
  const rootPath = nav?.root.path ?? '/';
  const atHome = !!nav && (pathname === rootPath || pathname === `${rootPath}/`);
  const sectionHere = useMemo(
    () => (nav && !hit ? nav.sections.find((s) => s.path && (pathname === s.path || pathname === `${s.path}/`)) ?? null : null),
    [nav, hit, pathname],
  );

  const crumbs: ReadonlyArray<Crumb> = useMemo(() => {
    if (crumbsOverride) return crumbsOverride;
    if (nav) return deriveBreadcrumbs(pathname, nav);
    const last = pathname.split('/').filter(Boolean).pop();
    return [{ label: last ? prettySegment(last) : 'Home' }];
  }, [crumbsOverride, nav, pathname]);

  const helpTitle = hit?.page.label ?? sectionHere?.label ?? (atHome ? `${nav?.root.label ?? appName} home` : crumbs[crumbs.length - 1]?.label ?? 'This page');
  const helpText = hit?.page.help ?? sectionHere?.holds ?? (atHome ? nav?.homeHelp : undefined);

  const onPickSearch = (item: SearchItem): void => {
    if (item.external) window.open(item.path, '_blank', 'noopener,noreferrer');
    else navigate(item.path);
    setDrawerOpen(false);
  };

  const openDrawer = (): void => {
    drawerInitial.current = drawerCloseRef.current;
    setDrawerOpen(true);
  };

  const displayName = user?.displayName ?? 'Signed in';

  const account = (
    <div className="ps-account" ref={menuRef} data-tour={userTour}>
      <button
        ref={menuButtonRef}
        type="button"
        className="ps-account__chip"
        aria-expanded={menuOpen}
        aria-controls="ps-account-menu"
        onClick={() => setMenuOpen((x) => !x)}
      >
        <span className="ps-avatar" aria-hidden="true">
          {initialsOf(displayName)}
        </span>
        <span className="ps-account__name">{displayName}</span>
        <span className="ps-visually-hidden">: account menu</span>
      </button>
      <div id="ps-account-menu" className="ps-account__menu" hidden={!menuOpen}>
        <div className="ps-account__who">
          <strong>{displayName}</strong>
          {user?.detail && <span>{user.detail}</span>}
        </div>
        {aboutPath && (
          <Link to={aboutPath} className="ps-account__item" onClick={() => setMenuOpen(false)}>
            About this app (v{version})
          </Link>
        )}
        <button type="button" className="ps-account__item" onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className={`ps-shell${phone && drawerOpen ? ' ps-shell--drawer-open' : ''}`}>
      <a className="ps-skip" href="#ps-main">
        Skip to content
      </a>

      {/* Phone header: menu button, logo, platform name, account. */}
      <div className="ps-mobilebar">
        <button
          ref={hamburgerRef}
          type="button"
          className="ps-iconbtn ps-iconbtn--dark"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          aria-controls="ps-sidebar"
          onClick={openDrawer}
        >
          <ShellIcon name="menu" size={22} />
        </button>
        <img className="ps-mobilebar__logo" src={logoUrl} alt="Phelps County Bank" width={44} height={15} />
        <span className="ps-mobilebar__title">{nav?.root.label ?? appName}</span>
        {phone && account}
      </div>

      {phone && drawerOpen && <div className="ps-drawer-backdrop" onClick={closeDrawer} aria-hidden="true" />}

      <nav
        id="ps-sidebar"
        ref={sidebarRef}
        className={`ps-sidebar${drawerOpen ? ' ps-sidebar--open' : ''}`}
        aria-label="Main menu"
        tabIndex={-1}
      >
        <div className="ps-sidebar__brand">
          <img className="ps-sidebar__logo" src={logoUrl} alt="Phelps County Bank" width={56} height={19} />
          <div className="ps-sidebar__app">
            <span className="ps-sidebar__name">{appName}</span>
            <span className="ps-sidebar__version">v{version}</span>
          </div>
          <button
            ref={drawerCloseRef}
            type="button"
            className="ps-iconbtn ps-iconbtn--dark ps-sidebar__close"
            aria-label="Close menu"
            onClick={() => {
              closeDrawer();
              hamburgerRef.current?.focus();
            }}
          >
            <ShellIcon name="close" size={20} />
          </button>
        </div>

        {platforms && platforms.length > 0 && (
          <div className="ps-switcher" role="group" aria-label="Platform" data-tour={homeTour}>
            {platforms.map((pl) => (
              <Link
                key={pl.id}
                to={pl.path}
                className={`ps-switcher__item${pl.id === currentPlatformId ? ' ps-switcher__item--on' : ''}`}
                aria-current={pl.id === currentPlatformId ? 'page' : undefined}
                onClick={closeDrawer}
              >
                {pl.label}
              </Link>
            ))}
          </div>
        )}

        <SearchBox
          ref={searchRef}
          items={items}
          onNavigate={onPickSearch}
          label="Search pages and tasks"
          placeholder="Search pages and tasks"
          shortcutHint={!phone}
        />

        {nav && (
          <div className="ps-nav">
            {!nav.hideHome && (
            <Link
              to={nav.root.path}
              className={`ps-nav__home${atHome ? ' ps-nav__link--active' : ''}`}
              aria-current={atHome ? 'page' : undefined}
              onClick={closeDrawer}
              data-tour={platforms && platforms.length > 0 ? undefined : homeTour}
            >
              <ShellIcon name="home" size={18} />
              Home
            </Link>
            )}
            {nav.sections.map((s) => (
              <SidebarSection key={s.id} section={s} activePath={activePath} Link={Link} onPick={closeDrawer} />
            ))}
          </div>
        )}

        {aboutPath && (
          <div className="ps-sidebar__foot">
            <Link
              to={aboutPath}
              className={`ps-nav__link${pathname === aboutPath ? ' ps-nav__link--active' : ''}`}
              aria-current={pathname === aboutPath ? 'page' : undefined}
              onClick={closeDrawer}
            >
              About this app
            </Link>
          </div>
        )}
      </nav>

      <div className="ps-maincol">
        <header className="ps-topbar">
          <nav aria-label="Breadcrumb" className="ps-crumbs">
            <ol>
              {crumbs.map((c, i) => {
                const last = i === crumbs.length - 1;
                return (
                  <li key={`${i}-${c.label}`}>
                    {last ? (
                      <span aria-current="page" className="ps-crumbs__current">
                        {c.label}
                      </span>
                    ) : c.path ? (
                      <Link to={c.path}>{c.label}</Link>
                    ) : (
                      <span>{c.label}</span>
                    )}
                    {!last && (
                      <span className="ps-crumbs__sep" aria-hidden="true">
                        /
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
          <div className="ps-topbar__right">
            {apiVersion && <span className="ps-topbar__api">{apiVersion}</span>}
            {topbarExtras}
            <button
              type="button"
              className="ps-help-btn"
              aria-label={`Help for this page: ${helpTitle}`}
              title="Help for this page"
              onClick={() => setHelpOpen(true)}
            >
              ?
            </button>
            {!phone && account}
          </div>
        </header>

        <main id="ps-main" className="ps-main" tabIndex={-1}>
          {children}
        </main>
        <footer className="ps-footer">
          Phelps County Bank · {appName} v{version}
          {aboutPath && (
            <>
              {' · '}
              <Link to={aboutPath}>About</Link>
            </>
          )}
        </footer>
      </div>

      <ModalShell
        open={helpOpen}
        title={`Help: ${helpTitle}`}
        onClose={() => setHelpOpen(false)}
        actions={
          <button type="button" className="ps-btn ps-btn--primary" onClick={() => setHelpOpen(false)}>
            Close
          </button>
        }
      >
        {helpText ? <p className="ps-help__text">{helpText}</p> : <p className="ps-help__text">There is no help written for this page yet.</p>}
        <h3 className="ps-help__h">Finding your way</h3>
        <ul className="ps-help__list">
          <li>
            Search for a page or a task with the search box in the menu{phone ? '' : ' (press Ctrl K)'}. Type it the way you would say it, for example
            &ldquo;holiday&rdquo; or &ldquo;give access&rdquo;.
          </li>
          <li>The breadcrumbs at the top show where you are; select one to go back up.</li>
          {changesNeedReason && <li>Changes ask for a reason. It is recorded with your name, the time, and the before and after values.</li>}
          <li>Something wrong or missing? Use the lifebuoy button at the bottom left to report it.</li>
        </ul>
      </ModalShell>
    </div>
  );
}
